import { isNaN, isNil } from 'lodash-es'
import bigjs from 'big.js'
import dayjs from 'dayjs'
/**
 * 将处理数据显示成40k+，40万+，40亿+的形式
 * @param value - 数值
 * @param digit - 保留的小数位数（可选，默认为2）
 * @returns {string}
 */
export function formatNumber(value: any, digit = 1) {
  if (isNil(value) || isNaN(value)) {
    return []
  }

  const { $i18n } = useNuxtApp()

  let valueString = typeof value === 'string' ? value.replace(/,/g, '').replace(/\.00$/, '') : value?.toString()
  let unit = ''
  // 检查值是否为负数
  const isNegative = Number.parseFloat(valueString) < 0
  value = Number.parseFloat(value)
  if (isNegative) {
    value = -value // 将负数转换为正数
  }

  // 中文单位
  if ($i18n.locale.value === 'zh') {
    if (value >= 1e8) {
      unit = '亿'
      valueString = (value / 1e8).toFixed(2)
    }
    else if (value >= 1e4) {
      unit = '万'
      valueString = (value / 1e4).toFixed(digit)
    }
    else {
      valueString = value.toFixed(digit)
    }
  }
  else if ($i18n.locale.value === 'pt') {
    if (value >= 1e12) {
      unit = 'T'
      valueString = (value / 1e12).toFixed(2)
    }
    else if (value >= 1e9) {
      unit = 'B'
      valueString = (value / 1e9).toFixed(2)
    }
    else if (value >= 1e8) {
      unit = 'mi'
      valueString = (value / 1e6).toFixed(digit)
    }
    else if (value >= 1e6) {
      unit = 'MM'
      valueString = (value / 1e6).toFixed(digit)
    }
    else if (value >= 1e3) {
      unit = 'mil'
      valueString = (value / 1e3).toFixed(digit)
    }
    else {
      valueString = value.toFixed(digit)
    }
  }
  else {
    if (value >= 1e12) {
      unit = 'T'
      valueString = (value / 1e12).toFixed(2)
    }
    else if (value >= 1e9) {
      unit = 'B'
      valueString = (value / 1e9).toFixed(2)
    }
    else if (value >= 1e6) {
      unit = 'M'
      valueString = (value / 1e6).toFixed(digit)
    }
    else if (value >= 1e3) {
      unit = 'K'
      valueString = (value / 1e3).toFixed(digit)
    }
    else {
      valueString = value.toFixed(digit)
    }
  }

  if (isNegative) {
    valueString = `-${valueString}` // 添加负号
  }

  // $1：替换掉匹配的部分，保留小数点后非零部分。
  valueString = valueString.replace(/(\.\d*?[1-9])0+|\.0+$/, '$1')
  return [formatNumberWithLocale(valueString), unit]
}

/**
 * 根据区域获取小数点和千分位分隔符
 * @param val - 区域（如 'pt'）
 * @returns string[] - [小数点分隔符, 千分位分隔符]
 */
export function getRegionDecimalPoint(val: string): string[] {
  return {
    pt: [',', '.'],
  }[val] || ['.', ',']
}

// 根据语言展示不同的数字格式
export function formatNumberWithLocale(value: string | number) {
  const { $i18n } = useNuxtApp()
  const locale = $i18n.locale.value
  const localeFormat: string[] = getRegionDecimalPoint(locale)
  const val = value ?? '-'
  const valueString = typeof val === 'string' ? val : val?.toString()
  return valueString.replace('.', localeFormat[0])
}

export function formatNumberWithUnit(value: any, digit = 1) {
  const res = formatNumber(value, digit).join('')
  return res === 'NaN' ? '-' : res || '-'
}

// 把时长转换成小时、分钟和秒数
export function formatMillisecondsToHMS(milliseconds: number) {
  const { t } = useI18n()
  // 总秒数
  const totalSeconds = Math.floor(milliseconds / 1000)

  // 计算小时、分钟和秒
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  // 格式化输出
  let formatted = ''

  if (hours > 0) {
    formatted += `${hours}${t('时')}`
  }
  if (minutes > 0 || hours > 0) {
    formatted += `${minutes}${t('分')}`
  }
  formatted += `${seconds}${t('秒')}`

  return formatted.trim()
}

/**
 * 根据过滤键和映射生成过滤选项的标签
 * @param key 过滤选项的键
 * @param option 包含过滤选项映射的对象
 * @param filter 当前应用的过滤条件
 * @returns 返回生成的标签对象或null
 */
export function buildOptionLabel(key: string, option: { [x: string]: any }, filter: Record<string, any>) {
  let label = option.label
  // 从过滤条件中获取最小值和最大值
  const prefix = option.prefix || 'min'
  const suffix = option.suffix || 'max'
  // 如果是单选类型
  if (option.isRadio) {
    const selectedRadioOption = option.options?.find((opt: { value: any }) => opt.value === filter[key])
    return selectedRadioOption ? { label: `${label}: ${selectedRadioOption.label}`, value: key, prefix, suffix } : null
  }
  const isUnitAppend = option.isPrice && filter.regionId && Number(filter.regionId) === 2
  const minValue = filter[`${prefix}${key}`] as number | undefined
  const maxValue = filter[`${suffix}${key}`] as number | undefined
  if (minValue === undefined && maxValue === undefined) {
    return null
  }
  // 获取选项的单位，默认为空
  const unit = option.unit || ''
  const { $i18n } = useNuxtApp()
  const selectOpt = option.options?.find((item: { maxValue?: number, minValue?: number }) => item.maxValue === maxValue && item.minValue === minValue)
  if (selectOpt) {
    return { label: `${$i18n.t(label)}: ${selectOpt.label}`, value: key, prefix, suffix }
  }
  // 根据最小值和最大值生成标签
  if (key === 'FirstDateCode') {
    label = `${label}: ${dayjs(minValue).format('YYYY/MM/DD')}-${dayjs(maxValue).format('YYYY/MM/DD')}`
  }
  else if (minValue !== undefined && maxValue !== undefined) {
    label = `${label}: ${isUnitAppend ? '' : unit}${formatNumberWithLocale(minValue)}${isUnitAppend ? unit : ''}-${isUnitAppend ? '' : unit}${formatNumberWithLocale(maxValue)}${isUnitAppend ? unit : ''}`
  }
  else if (minValue !== undefined) {
    label = `${label}: ${$i18n.t('以上', [`${isUnitAppend ? '' : unit}${formatNumberWithLocale(minValue)}${isUnitAppend ? unit : ''}`])}`
  }
  else if (maxValue !== undefined) {
    label = `${label}: ${$i18n.t('以下', [`${isUnitAppend ? '' : unit}${formatNumberWithLocale(maxValue)}${isUnitAppend ? unit : ''}`])}`
  }

  return label ? { label, value: key, prefix, suffix } : null
}

/**
 * 百分数转成小数
 */
export function percentToDecimal(percent: string) {
  // 去掉百分号并将百分数转换为数字
  const value = bigjs(percent.replace('%', ''))

  // 将百分数转换为小数
  return value.div(100).toNumber()
}

/**
 * 小数转成百分数
 *
 * @param decimal 小数
 * @param fractionDigits 保留小数位数
 */
export function decimalToPercent(decimal: bigjs.BigSource, fractionDigits = 1) {
  // 去掉百分号并将百分数转换为数字
  const value = bigjs(decimal)

  // 将百分数转换为小数
  return `${formatNumberWithUnit(value.mul(100).toNumber(), fractionDigits)}%`
}

/**
 * 根据给定的比率格式化值
 *
 * @param value 待格式化的数值
 * @param conversionRate 用于将输入的值乘以该比率，默认为100
 * @returns 格式化后的数字值
 */
export function convertValueWithRate(value: bigjs.BigSource, conversionRate = 100) {
  return value ? bigjs(value).mul(conversionRate).toNumber() : value
}

/**
 * 计算从当前日期到未来某个时间戳的天数差
 *
 * 此函数接受一个表示未来时间的时间戳参数，可以是字符串、数字、Date对象、dayjs对象，或者null/undefined（后两者表示未提供有效时间）
 * 它通过比较未来日期和当前日期，计算出剩余的天数
 *
 * @param futureTimestamp 未来时间的时间戳，可以接受多种格式
 * @returns 返回从当前日期到未来日期的天数差
 */
export function calculateDaysRemaining(futureTimestamp: number) {
  const futureDate = dayjs(futureTimestamp)
  const now = dayjs()
  const daysRemaining = futureDate.diff(now, 'day')
  return daysRemaining
}

/**
 * 格式化价格区间字符串
 *
 * 该函数接受一个价格区间字符串，并对其进行格式化处理可以指定货币符号和汇率，以适应不同的货币需求
 * 价格区间可以是范围形式（如 '￥100-￥200'），也可以是单个价格（如 '￥150'），或是带有方向的价格（如 '￥100以上'、'￥200以下'）
 *
 * @param zone 价格区间字符串，可以包含货币符号和连字符表示范围
 * @param currencySymbol 货币符号，默认为空字符串，用于替换输入中的货币符号并添加到输出中
 * @param isUnitAppend 单位是否在尾部追加，用于越南下
 * @param rate 汇率，默认为1，用于将输入的价格转换为其他货币的价格
 * @returns 格式化后的价格区间字符串，如果输入不合法或处理中出现错误，返回空字符串
 */
export function formatZone(zone: string, currencySymbol = '', isUnitAppend?: boolean) {
  function getUnitPosition() {
    return {
      prefix: isUnitAppend ? '' : currencySymbol,
      suffix: isUnitAppend ? currencySymbol : '',
    }
  }
  try {
    if (!zone || import.meta.server)
      return ''
    if (zone.includes('-')) {
      const [minStr, maxStr] = zone.split('-')
      const min = Number(minStr.replace(currencySymbol, '').trim())
      const max = Number(maxStr.replace(currencySymbol, '').trim())
      return `${getUnitPosition().prefix}${formatNumberWithUnit(min)}${getUnitPosition().suffix}-${getUnitPosition().prefix}${formatNumberWithUnit(max)}${getUnitPosition().suffix}`
    }
    else if (zone.startsWith('>') || zone.startsWith('<')) {
      const valueStr = zone.replace(currencySymbol, '').trim()
      const prefix = zone.slice(0, 1)
      const value = Number(valueStr.replace(prefix, ''))
      return `${prefix}${getUnitPosition().prefix}${formatNumberWithUnit(value)}${getUnitPosition().suffix}`
    }
    else {
      return `${getUnitPosition().prefix}${formatNumberWithUnit(zone)}${getUnitPosition().suffix}`
    }
  }
  catch (error) {
    console.error(`Error processing zone: ${zone}`, error)
    return ''
  }
}

/**
 * 将给定的数值格式化为带有逗号分隔符的价格字符串
 * 如果提供的值为空或无效，则返回 "-"
 *
 * @param value 任何可以转换为数字的值
 * @returns 格式化后的价格字符串，如果输入无效则返回 "-"
 */
export function formatterPrice(value: any) {
  if (!value)
    return '-'
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
